import { useState, useCallback, useMemo, type RefObject } from 'react';
import { Plus } from 'lucide-react';
import IssueTreeItem, { CHILD_TYPES } from './IssueTreeItem';
import ChildTypeDropdown from './ChildTypeDropdown';
import InlineIssueCreate from './InlineIssueCreate';
import type { FlatNode, TimeUnit } from './timelineUtils';
import { getHeaderHeight } from './timelineUtils';
import type { ProjectIssueType } from '../../../hooks/useProjectIssueTypes';

interface InlineCreateState {
  parentIssueId: string | null;
  issueType: string;
  depth: number;
  afterIssueId: string;
}

interface Props {
  flattenedNodes: FlatNode[];
  expandedIds: Set<string>;
  timeUnit: TimeUnit;
  onToggle: (issueId: string) => void;
  onIssueClick: (issueId: string) => void;
  onScroll: () => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  onCreateIssue?: (parentIssueId: string | null, issueType: string, title: string) => Promise<void>;
  projectIssueTypes?: ProjectIssueType[];  // CHG-052
}

export default function IssueTree({
  flattenedNodes,
  expandedIds,
  timeUnit,
  onToggle,
  onIssueClick,
  onScroll,
  scrollRef,
  onCreateIssue,
  projectIssueTypes,
}: Props) {
  const [dropdownTarget, setDropdownTarget] = useState<string | null>(null);
  const [inlineCreate, setInlineCreate] = useState<InlineCreateState | null>(null);

  // CHG-052: 동적 자식 타입 조회 (API 기반 → CHILD_TYPES 폴백)
  const getChildTypes = useCallback(
    (issueType: string): { value: string; label: string }[] => {
      if (projectIssueTypes) {
        const parentType = projectIssueTypes.find((t) => t.name === issueType);
        if (parentType && parentType.allowedChildTypes.length > 0) {
          return parentType.allowedChildTypes.map((c) => ({ value: c.name, label: c.name }));
        }
        // allowedChildTypes가 비어있으면 하위 생성 불가
        if (parentType) return [];
      }
      // 폴백: 하드코딩 CHILD_TYPES
      return CHILD_TYPES[issueType] ?? [];
    },
    [projectIssueTypes],
  );

  // CHG-052: 루트에서 생성 가능한 타입 목록
  const rootTypes = useMemo(() => {
    if (projectIssueTypes) {
      return projectIssueTypes
        .filter((t) => t.canBeRoot)
        .map((t) => ({ value: t.name, label: t.name }));
    }
    return [{ value: 'Epic', label: 'Epic' }];
  }, [projectIssueTypes]);

  // CHG-052: 기본 루트 타입
  // 타임라인 하단 [+ 작업 추가]는 Epic을 root로 생성하는 것이 자연스러움
  // (Task는 Epic 하위로 +버튼을 통해 생성하는 패턴). Epic이 루트 가능하면 우선 선택.
  const defaultRootType = useMemo(() => {
    if (projectIssueTypes) {
      const epic = projectIssueTypes.find((t) => t.canBeRoot && (t.name === 'Epic' || t.name === 'EPIC'));
      if (epic) return epic.name;
      const def = projectIssueTypes.find((t) => t.isDefault && t.canBeRoot);
      if (def) return def.name;
      const firstRoot = projectIssueTypes.find((t) => t.canBeRoot);
      if (firstRoot) return firstRoot.name;
    }
    return 'Epic';
  }, [projectIssueTypes]);

  const hasChildTypes = useCallback(
    (issueType: string): boolean => getChildTypes(issueType).length > 0,
    [getChildTypes],
  );

  const handleAddChild = useCallback(
    (issueId: string, issueType: string) => {
      if (!expandedIds.has(issueId)) {
        onToggle(issueId);
      }

      const childTypes = getChildTypes(issueType);
      if (childTypes.length === 0) return;

      setInlineCreate(null);

      if (childTypes.length === 1) {
        const parentNode = flattenedNodes.find((n) => n.issueId === issueId);
        const depth = parentNode ? parentNode.depth + 1 : 1;
        setInlineCreate({
          parentIssueId: issueId,
          issueType: childTypes[0].value,
          depth,
          afterIssueId: issueId,
        });
        setDropdownTarget(null);
      } else {
        setDropdownTarget(issueId);
      }
    },
    [expandedIds, onToggle, flattenedNodes, getChildTypes],
  );

  const handleSelectType = useCallback(
    (type: string) => {
      if (!dropdownTarget) return;
      const parentNode = flattenedNodes.find((n) => n.issueId === dropdownTarget);
      const depth = parentNode ? parentNode.depth + 1 : 1;
      setInlineCreate({
        parentIssueId: dropdownTarget,
        issueType: type,
        depth,
        afterIssueId: dropdownTarget,
      });
      setDropdownTarget(null);
    },
    [dropdownTarget, flattenedNodes],
  );

  const handleInlineSubmit = useCallback(
    async (title: string) => {
      if (!inlineCreate || !onCreateIssue) return;
      try {
        await onCreateIssue(inlineCreate.parentIssueId, inlineCreate.issueType, title);
      } finally {
        setInlineCreate(null);
      }
    },
    [inlineCreate, onCreateIssue],
  );

  const handleInlineCancel = useCallback(() => {
    setInlineCreate(null);
  }, []);

  const handleRootAdd = useCallback(() => {
    setDropdownTarget(null);
    if (rootTypes.length === 1) {
      setInlineCreate({
        parentIssueId: null,
        issueType: rootTypes[0].value,
        depth: 0,
        afterIssueId: '',
      });
    } else if (rootTypes.length > 1) {
      // 루트 타입이 여러 개면 기본 타입으로 바로 생성
      setInlineCreate({
        parentIssueId: null,
        issueType: defaultRootType,
        depth: 0,
        afterIssueId: '',
      });
    }
  }, [rootTypes, defaultRootType]);

  return (
    <div className="w-[280px] min-w-[280px] border-r bg-white flex flex-col">
      <div
        className="bg-gray-50 border-b px-4 flex items-center shrink-0"
        style={{ height: `${getHeaderHeight(timeUnit)}px` }}
      >
        <span className="text-sm font-medium text-gray-500 uppercase tracking-wider">작업</span>
      </div>
      <div
        ref={scrollRef as React.RefObject<HTMLDivElement>}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        onScroll={onScroll}
      >
        {flattenedNodes.length === 0 && !inlineCreate ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <p className="text-sm">작업이 없습니다.</p>
            <p className="text-sm mt-1">아래 버튼으로 작업을 생성해보세요.</p>
          </div>
        ) : (
          flattenedNodes.map((node) => (
            <div key={node.issueId} className="relative">
              <IssueTreeItem
                node={node}
                isExpanded={expandedIds.has(node.issueId)}
                onToggle={() => onToggle(node.issueId)}
                onClick={() => onIssueClick(node.issueId)}
                onAddChild={
                  onCreateIssue && hasChildTypes(node.issueType)
                    ? () => handleAddChild(node.issueId, node.issueType)
                    : undefined
                }
              />
              {dropdownTarget === node.issueId && (
                <ChildTypeDropdown
                  types={getChildTypes(node.issueType)}
                  onSelect={handleSelectType}
                  onClose={() => setDropdownTarget(null)}
                />
              )}
              {inlineCreate?.afterIssueId === node.issueId && (
                <InlineIssueCreate
                  issueType={inlineCreate.issueType}
                  depth={inlineCreate.depth}
                  onSubmit={handleInlineSubmit}
                  onCancel={handleInlineCancel}
                />
              )}
            </div>
          ))
        )}

      </div>

      {/* 루트 inline-create + [+ 작업 추가] — sticky footer (스크롤 안에 두면
          캘린더 영역과 줄이 어긋남, 또한 항상 노출되어야 사용성이 좋음) */}
      {onCreateIssue && (inlineCreate?.afterIssueId === '' || rootTypes.length > 0) && (
        <div className="border-t border-gray-100 bg-white shrink-0">
          {inlineCreate?.afterIssueId === '' ? (
            <InlineIssueCreate
              issueType={inlineCreate.issueType}
              depth={inlineCreate.depth}
              onSubmit={handleInlineSubmit}
              onCancel={handleInlineCancel}
            />
          ) : (
            <button
              onClick={handleRootAdd}
              className="flex items-center gap-1.5 w-full px-4 py-2 text-sm text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Plus size={14} />
              작업 추가
            </button>
          )}
        </div>
      )}
    </div>
  );
}
